import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Check, X, CheckCheck } from 'lucide-react';
import { PageTitle, Card } from '../ui';
import { useNotifications } from '../notifications/NotificationsContext';
import {
  getNotifIcon, getNotifColor, getNotifLabel, getNotifSource,
  NOTIF_SOURCE_OPTIONS, type NotifSource,
} from '../notifications/notifPresentation';

export default function Notifications() {
  const { notifications, unreadCount, markAsRead, markAllAsRead, deleteNotification, loading } = useNotifications();
  const navigate = useNavigate();
  const [filter, setFilter] = useState<'all' | 'unread'>('all');
  const [sourceFilter, setSourceFilter] = useState<NotifSource | 'all'>('all');

  const filtered = useMemo(() => {
    return notifications.filter((n) => {
      if (filter === 'unread' && n.lu) return false;
      if (sourceFilter !== 'all' && getNotifSource(n) !== sourceFilter) return false;
      return true;
    });
  }, [notifications, filter, sourceFilter]);

  const onClick = async (n: typeof notifications[0]) => {
    if (!n.lu) await markAsRead(n.id);
    if (n.lien) navigate(n.lien);
  };

  return (
    <div>
      <PageTitle
        title="Notifications"
        subtitle={`${notifications.length} au total · ${unreadCount} non lue${unreadCount > 1 ? 's' : ''}`}
      />

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <button
          onClick={() => setFilter('all')}
          className={`px-3 py-1.5 rounded-lg text-xs ${filter === 'all' ? 'bg-amber-500 text-slate-950 font-medium' : 'bg-slate-800 text-slate-300 hover:bg-slate-700'}`}
        >Toutes ({notifications.length})</button>
        <button
          onClick={() => setFilter('unread')}
          className={`px-3 py-1.5 rounded-lg text-xs ${filter === 'unread' ? 'bg-amber-500 text-slate-950 font-medium' : 'bg-slate-800 text-slate-300 hover:bg-slate-700'}`}
        >Non lues ({unreadCount})</button>

        <span className="text-slate-700 mx-1">|</span>

        <select
          value={sourceFilter}
          onChange={(e) => setSourceFilter(e.target.value as any)}
          className="px-3 py-1.5 bg-slate-900 border border-slate-800 rounded-lg text-xs text-slate-200 focus:outline-none focus:border-amber-500/50"
        >
          <option value="all">Tous les types</option>
          {NOTIF_SOURCE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>

        <div className="flex-1" />

        {unreadCount > 0 && (
          <button
            onClick={markAllAsRead}
            className="inline-flex items-center gap-2 px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-lg text-xs"
          >
            <CheckCheck size={13} strokeWidth={1.5} /> Tout marquer comme lu
          </button>
        )}
      </div>

      <Card className="p-0 overflow-hidden">
        {loading && <div className="px-4 py-8 text-center text-sm text-slate-500">Chargement…</div>}
        {!loading && filtered.length === 0 && (
          <div className="px-4 py-12 text-center">
            <Bell size={32} strokeWidth={1.5} className="mx-auto text-slate-700 mb-3" />
            <p className="text-sm text-slate-400">Aucune notification.</p>
          </div>
        )}
        {filtered.map((n) => {
          const Icon = getNotifIcon(n);
          const colorCls = getNotifColor(n, 'full');
          return (
            <div
              key={n.id}
              className={`group flex gap-3 px-4 py-3 border-b border-slate-800/60 hover:bg-slate-800/30 cursor-pointer ${!n.lu ? 'bg-slate-800/20' : ''}`}
              onClick={() => onClick(n)}
            >
              <div className={`shrink-0 w-9 h-9 rounded-lg border flex items-center justify-center ${colorCls}`}>
                <Icon size={15} strokeWidth={1.5} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2 mb-0.5">
                  <p className={`text-sm ${!n.lu ? 'text-slate-100 font-medium' : 'text-slate-300'}`}>{n.titre}</p>
                  <span className="text-[10px] text-slate-500 shrink-0">{new Date(n.created_at).toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                </div>
                {n.message && <p className="text-xs text-slate-400">{n.message}</p>}
                <p className="text-[10px] text-slate-600 mt-1 uppercase tracking-wider">{getNotifLabel(n)}</p>
              </div>
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100">
                {!n.lu && (
                  <button
                    onClick={(e) => { e.stopPropagation(); markAsRead(n.id); }}
                    className="w-7 h-7 rounded hover:bg-slate-700 text-slate-400 hover:text-slate-200 flex items-center justify-center"
                    title="Marquer comme lu"
                  ><Check size={14} strokeWidth={1.5} /></button>
                )}
                <button
                  onClick={(e) => { e.stopPropagation(); deleteNotification(n.id); }}
                  className="w-7 h-7 rounded hover:bg-slate-700 text-slate-400 hover:text-red-400 flex items-center justify-center"
                  title="Supprimer"
                ><X size={14} strokeWidth={1.5} /></button>
              </div>
            </div>
          );
        })}
      </Card>
    </div>
  );
}
